import React, { useCallback, useEffect, useRef, useState } from 'react';

import { tableRootVariants } from './tableStyles';

const getVerticalScrollParent = (element: HTMLElement) => {
  let parent = element.parentElement;

  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);
    const canScroll = parent.scrollHeight > parent.clientHeight;

    if (canScroll && ['auto', 'scroll', 'overlay'].includes(overflowY)) {
      return parent;
    }

    parent = parent.parentElement;
  }

  return null;
};

type TableRootProps = {
  children: React.ReactNode;
  className?: string;
  containerClassName?: string;
};

const TableRoot: React.FC<TableRootProps> = ({
  children,
  className,
  containerClassName,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const verticalScrollParentRef = useRef<HTMLElement | null>(null);
  const [scrollState, setScrollState] = useState({
    left: false,
    right: false,
    top: false,
  });
  const styles = tableRootVariants();
  const updateScrollState = useCallback(() => {
    const element = containerRef.current;
    if (!element) return;

    const maxScrollLeft = element.scrollWidth - element.clientWidth;
    const verticalScrollElement = verticalScrollParentRef.current ?? element;
    const nextState = {
      left: element.scrollLeft > 0,
      right: element.scrollLeft < maxScrollLeft - 1,
      top: verticalScrollElement.scrollTop > 0,
    };

    setScrollState(current =>
      current.left === nextState.left &&
      current.right === nextState.right &&
      current.top === nextState.top
        ? current
        : nextState
    );
  }, []);

  useEffect(() => {
    updateScrollState();

    const element = containerRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const verticalScrollParent = getVerticalScrollParent(element);
    verticalScrollParentRef.current = verticalScrollParent;

    const observer = new ResizeObserver(updateScrollState);
    observer.observe(element);
    if (verticalScrollParent) {
      observer.observe(verticalScrollParent);
      verticalScrollParent.addEventListener('scroll', updateScrollState, {
        passive: true,
      });
    }

    return () => {
      observer.disconnect();
      verticalScrollParent?.removeEventListener('scroll', updateScrollState);
    };
  }, [updateScrollState]);

  return (
    <div
      ref={containerRef}
      data-scrolled-left={scrollState.left}
      data-scrolled-right={scrollState.right}
      data-scrolled-top={scrollState.top}
      onScroll={updateScrollState}
      className={styles.container({ class: containerClassName })}
    >
      <table className={styles.table({ class: className })}>{children}</table>
    </div>
  );
};

export default React.memo(TableRoot);
